import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import ContentHeader from './ContentHeader'
import {
  useClearUserInfo,
  useI18n,
  useProfile,
  useRehabSelectedThemes,
} from 'contexts/AuthContext'
import { Button } from 'antd'
import moment from 'moment-timezone'
import _ from 'lodash'
import { ScreenKey } from './util'
import { ThreeOptionSelect } from 'components/SwitchSelect/index'
import HeightInputNumber from 'components/HeightInput/index'
import CareInput from 'components/CareInput/index'
import { logAmplitudeEvent, setAmplitudeProfile } from 'utils/amplitudeInit'
import { getBMI, isMobile } from 'utils/basic'
import { setBrazeProfile } from 'utils/brazeInit'
import {
  checkNeedToRelogin,
  getHealthieRequest,
  showErrorMessage,
  useUpdateClient,
} from 'hooks/careHooks'
import { getHealthieUserId, getLocalLanguage } from 'utils/authLocalStorage'
import { updateUserPromise } from 'apis/FitOnCareAPI'
import {
  HEALTHIE_CREATE_ENTRY_STRING,
  useHealthieAPIs,
} from 'apis/HealthieApiClient'
import { AuthContext } from 'contexts/AuthContext'
import BirthdayInput from 'components/BirthdayInput/index'
export const GenderOptions = [
  { icon: 'female', title: 'Female', value: 'Female' },
  { icon: 'male', title: 'Male', value: 'Male' },
  { icon: 'other', title: 'Other', value: 'Other' },
]

export const useCreateEntry = () => {
  const { i18n } = useI18n()
  const { createEntry } = useHealthieAPIs()
  const { clearAll } = useClearUserInfo()

  const createHealtieEntry = async ({ weight, onSuccess = () => {} } = {}) => {
    try {
      const { error, data } = await getHealthieRequest({
        healthieRequest: createEntry,
        queryString: HEALTHIE_CREATE_ENTRY_STRING,
        variables: {
          user_id: getHealthieUserId(),
          type: 'MetricEntry',
          category: 'Weight',
          metric_stat: weight?.toString(),
          description: 'Source: Patient Signup',
        },
        operationName: 'createEntry',
      })
      if (data) {
        if (data.createEntry?.messages) {
          showErrorMessage(data.createEntry?.messages, i18n)
          checkNeedToRelogin({ data, operationName: 'createEntry', clearAll })
        } else {
          onSuccess()
        }
      } else if (error) {
        showErrorMessage(error, i18n)
      }
    } catch (error) {
      showErrorMessage(error, i18n)
    }
  }

  return { createHealtieEntry }
}

export const useCareProfile = () => {
  const { profile, setProfile } = useProfile()
  const [birthday, setBirthday] = useState(profile.birthday)
  const [gender, setGender] = useState(profile.gender || 'Female')
  const [heightFt, setHeightFt] = useState(profile.heightFt)
  const [heightIn, setHeightIn] = useState(profile.heightIn)
  const [weightLbs, setWeightLbs] = useState(profile.weightLbs)
  const { updateClient } = useUpdateClient()
  const { createHealtieEntry } = useCreateEntry()
  const healthieUserId = getHealthieUserId()
  const authContext = useContext(AuthContext)
  const getInches = useCallback(() => {
    return Number(heightFt) * 12 + Number(heightIn)
  }, [heightFt, heightIn])

  const isNextButtonEnable = useMemo(() => {
    return (
      !_.isEmpty(birthday) &&
      !_.isEmpty(gender) &&
      heightFt &&
      +heightIn >= 0 &&
      weightLbs &&
      moment(birthday, 'MM/DD/YYYY', true).isValid() &&
      moment(birthday, 'MM/DD/YYYY', true).isBefore(moment())
    )
  }, [birthday, gender, heightFt, heightIn, weightLbs])

  const uploadProfile = async (onSuccess) => {
    setProfile({
      birthday,
      gender,
      heightFt,
      heightIn,
      weightLbs,
    })

    updateClient({
      variables: {
        id: healthieUserId,
        dob: moment(birthday, 'MM/DD/YYYY').format('YYYY-MM-DD'),
        gender: gender,
        height: getInches().toString(),
      },
      onSuccess: () => {
        onSuccess?.()
      },
    })

    const _gender = gender === 'Male' ? 1 : gender === 'Female' ? 2 : 3
    updateUserPromise({ gender: _gender }, authContext)

    createHealtieEntry({
      weight: weightLbs,
      onSuccess: () => {
        console.log('create entry success')
      },
    })
  }

  return {
    birthday,
    setBirthday,
    gender,
    setGender,
    heightFt,
    setHeightFt,
    heightIn,
    setHeightIn,
    weightLbs,
    setWeightLbs,
    isNextButtonEnable,
    uploadProfile,
  }
}

const CompleteProfile = (props) => {
  const { utmSource, sku } = props
  const { i18n } = useI18n()
  const heightFtRef = useRef(null)
  const heightInRef = useRef(null)
  const weightRef = useRef(null)

  const {
    birthday,
    setBirthday,
    gender,
    setGender,
    heightFt,
    setHeightFt,
    heightIn,
    setHeightIn,
    weightLbs,
    setWeightLbs,
    isNextButtonEnable,
    uploadProfile,
  } = useCareProfile()

  const { rehabSelectedThemes } = useRehabSelectedThemes()

  const { theme } = useMemo(() => {
    return {
      theme: rehabSelectedThemes?.map((theme) => theme.name).join(','),
    }
  }, [rehabSelectedThemes])

  useEffect(() => {
    logAmplitudeEvent('Screen View: Onboarding - Health Profile', {
      sku: sku,
      theme,
      language: getLocalLanguage(),
    })
  }, [sku, theme])

  const onNext = () => {
    switch (props.sku) {
      case 'msk':
        if (utmSource) {
          props.setCurrentKey(ScreenKey.TALK_ABOUT_COVERAGE)
        } else {
          props.setCurrentKey(ScreenKey.HEAR_ABOUT)
        }

        break
      case 'wlc':
        if (utmSource) {
          props.setCurrentKey(ScreenKey.PAST_DIAGNOSIS)
        } else {
          props.setCurrentKey(ScreenKey.HEAR_ABOUT)
        }
        break
      case 'pt':
        if (utmSource) {
          props.setCurrentKey(ScreenKey.PAIN_LOCATED)
        } else {
          props.setCurrentKey(ScreenKey.HEAR_ABOUT)
        }
        break
      default:
        if (utmSource) {
          props.setCurrentKey(ScreenKey.TALK_ABOUT_COVERAGE)
        } else {
          props.setCurrentKey(ScreenKey.HEAR_ABOUT)
        }
    }
  }

  return (
    <div className='info-sec'>
      <ContentHeader {...props} />
      <div className='info-content'>
        <div className='title'>
          {i18n('Please complete your health profile')}
        </div>
        <div
          className='profile-item'
          style={{
            marginTop: '32px',
            height: 'auto',
            minHeight: '56px',
            alignItems: 'flex-start',
            gap: '16px',
          }}
        >
          <div
            className='item-left'
            style={{
              height: '56px',
              lineHeight: '56px',
            }}
          >
            {i18n('Date of birth')}
          </div>
          <BirthdayInput
            birthday={birthday}
            setBirthday={setBirthday}
            focus={true}
          />
        </div>
        <div className='profile-item'>
          <div className='item-left'>{i18n('Gender')}</div>
          <ThreeOptionSelect
            className='item-right'
            options={GenderOptions}
            value={gender}
            onChange={(value) => {
              setGender(value)
            }}
          />
        </div>
        <div className='profile-item'>
          <div className='item-left'>{i18n('Height')}</div>
          <div className='height-sec'>
            <HeightInputNumber
              inputRef={heightFtRef}
              value={heightFt}
              setValue={(value) => {
                setHeightFt(value)
                if (value.length === 1) {
                  heightInRef.current?.focus()
                }
              }}
              placeholder='0'
              unit={'ft'}
              max={7}
              min={4}
            />
            <HeightInputNumber
              value={heightIn}
              setValue={(value) => {
                setHeightIn(value)
                if (value.length > 0 && value !== '1') {
                  weightRef.current?.focus()
                }
              }}
              placeholder='0'
              unit={'in'}
              max={11}
              min={0}
              inputRef={heightInRef}
            />
          </div>
        </div>
        <div className='profile-item'>
          <div className='item-left'>{i18n('Weight')}</div>
          <div className='height-sec'>
            <HeightInputNumber
              value={weightLbs}
              setValue={setWeightLbs}
              placeholder='0'
              unit={'lbs'}
              max={500}
              min={80}
              inputRef={weightRef}
            />
          </div>
        </div>
        <Button
          className='btn-primary full-width-btn m-t-24'
          style={isMobile() ? { width: '100%' } : {}}
          onClick={() => {
            uploadProfile(() => onNext())
            const bmi = getBMI(
              Number(weightLbs),
              Number(heightFt) * 12 + Number(heightIn)
            )
            const height = Number(heightFt) * 12 + Number(heightIn)
            const _birthday = moment(birthday, 'MM/D/YYYY').format('YYYY-MM-DD')
            const age = moment().diff(birthday, 'years')

            logAmplitudeEvent('Click: Onboarding - Health Profile', {
              Age: age,
              Gender: gender.substring(0, 1),
              BMI: bmi,
              sku: props.sku,
              theme,
              language: getLocalLanguage(),
            })
            setAmplitudeProfile({
              age: age,
              birthday: _birthday,
              gender,
              bmi,
              height,
              weight: weightLbs,
            })
            setBrazeProfile({
              dob: _birthday,
              gender: gender.substring(0, 1),
              bmi,
              height,
              weight: weightLbs,
            })
          }}
          disabled={!isNextButtonEnable}
        >
          {i18n('Next')}
        </Button>
      </div>
    </div>
  )
}

export default CompleteProfile
